/**
 * API Client Service
 * Centralized HTTP client for backend API calls
 * Features: Auto auth token injection, retry logic, request deduplication
 * SECURITY: No direct Supabase database queries - all data goes through backend
 */

import { tokenService } from './tokenService';
import { reportApiError, reportApiSuccess } from '@/shared/contexts/ApiHealthContext';

/**
 * API Client Configuration
 */
const API_BASE_URL = import.meta.env.VITE_SIGNALING_SERVER_URL ?? 'https://webrtc.wlkthru.co';
const API_CONFIG = {
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000,
  retryAttempts: 1,
};

/**
 * API Error Class
 */
export class APIError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public code?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'APIError';
  }
}

/**
 * API Client Class
 */
class ApiClient {
  private baseURL: string;
  private timeout: number;
  private retryAttempts: number;
  private pendingRequests: Map<string, Promise<any>>;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
    this.timeout = API_CONFIG.timeout;
    this.retryAttempts = API_CONFIG.retryAttempts;
    this.pendingRequests = new Map();
  }

  /**
   * Get current auth token using centralized token service
   * Prevents race conditions and ensures token is fresh
   */
  private async getAuthToken(): Promise<string | null> {
    return await tokenService.getToken();
  }

  /**
   * Make HTTP request with auth token injection
   * CRITICAL: Handles 401 with automatic token refresh
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    attemptNumber: number = 1
  ): Promise<T> {
    try {
      let token = await this.getAuthToken();

      const url = `${this.baseURL}${endpoint}`;
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...options.headers,
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle 401 - Token expired, refresh and retry
      if (response.status === 401 && token) {
        console.log('[API] 401 Unauthorized - attempting token refresh...');

        try {
          // Use centralized token service to prevent race conditions
          const newToken = await tokenService.refreshToken();

          console.log('[API] Token refreshed successfully, retrying request...');

          // Retry the request with new token
          const retryController = new AbortController();
          const retryTimeoutId = setTimeout(() => retryController.abort(), this.timeout);

          const retryResponse = await fetch(url, {
            ...options,
            headers: {
              ...headers,
              Authorization: `Bearer ${newToken}`,
            },
            signal: retryController.signal,
          });

          clearTimeout(retryTimeoutId);

          if (!retryResponse.ok) {
            const errorData = await retryResponse.json().catch(() => ({}));
            throw new APIError(
              errorData.error || `Request failed after token refresh: ${retryResponse.status}`,
              retryResponse.status,
              errorData.code,
              errorData.details
            );
          }

          const retryData = await retryResponse.json();
          reportApiSuccess(); // Report successful retry
          return retryData;
        } catch (refreshError: any) {
          console.error('[API] Token refresh failed:', refreshError);
          const sessionError = new APIError(
            'Session expired. Please log in again.',
            401,
            'SESSION_EXPIRED'
          );
          // Don't report 401 as server error - it's expected auth behavior
          throw sessionError;
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const apiError = new APIError(
          errorData.error || `Request failed: ${response.status}`,
          response.status,
          errorData.code
        );
        // Report server errors (5xx) to health tracker
        if (response.status >= 500) {
          reportApiError(apiError);
        }
        throw apiError;
      }

      const data = await response.json();
      reportApiSuccess(); // Report successful request
      return data;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        const timeoutError = new APIError('Request timeout', 408, 'TIMEOUT');
        reportApiError(timeoutError); // Report timeout as potential server issue
        throw timeoutError;
      }

      if (error instanceof APIError) {
        throw error;
      }

      // Retry on network errors
      if (attemptNumber < this.retryAttempts) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.request<T>(endpoint, options, attemptNumber + 1);
      }

      const networkError = new APIError(
        error.message || 'Network error',
        0,
        'NETWORK_ERROR'
      );
      reportApiError(networkError); // Report network error
      throw networkError;
    }
  }

  /**
   * GET with request deduplication
   */
  async get<T>(endpoint: string): Promise<T> {
    const cacheKey = `GET:${endpoint}`;

    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey)!;
    }

    const requestPromise = this.request<T>(endpoint, { method: 'GET' })
      .finally(() => {
        this.pendingRequests.delete(cacheKey);
      });

    this.pendingRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

export const apiClient = new ApiClient();
